// Admin billing API — typed wrappers around /admin/v1/billing/*.

import type { ApiClient } from '../api-client.js';

export type SubscriptionPlanFilter = 'all' | 'free' | 'starter' | 'pro';
export type SubscriptionFreezeFilter = 'all' | 'active' | 'frozen';
export type SubscriptionStatusFilter = 'all' | 'active' | 'past_due' | 'canceled';

export interface SubscriptionRow {
  tenantId: string;
  tenantName: string;
  planCode: string;
  status: string;
  stripeCustomerId: string;
  stripeSubscriptionId: string | null;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAt: string | null;
  frozenAt: string | null;
  frozenReason: string | null;
  costThisPeriodUsd: number;
  creditGrantedMicros: number;
  updatedAt: string;
}

export interface ReconciliationRow {
  id: string;
  tenantId: string;
  tenantName: string;
  runDate: string;
  eventKind: string;
  localTotal: number;
  stripeTotal: number;
  driftPct: string;
  status: string;
  notes: string | null;
  startedAt: string;
  finishedAt: string | null;
}

export interface ListSubscriptionsParams {
  plan?: SubscriptionPlanFilter;
  freeze?: SubscriptionFreezeFilter;
  status?: SubscriptionStatusFilter;
  limit?: number;
}

export const SUBSCRIPTIONS_QUERY_KEY = ['admin', 'billing', 'subscriptions'] as const;
export const RECONCILIATION_QUERY_KEY = ['admin', 'billing', 'reconciliation'] as const;

function buildSubsQuery(params: ListSubscriptionsParams): string {
  const sp = new URLSearchParams();
  if (params.plan && params.plan !== 'all') sp.set('plan', params.plan);
  if (params.freeze && params.freeze !== 'all') sp.set('freeze', params.freeze);
  if (params.status && params.status !== 'all') sp.set('status', params.status);
  if (params.limit !== undefined) sp.set('limit', String(params.limit));
  const tail = sp.toString();
  return tail.length > 0 ? `?${tail}` : '';
}

export const billingApi = {
  async listSubscriptions(
    client: ApiClient,
    params: ListSubscriptionsParams = {},
  ): Promise<{ rows: SubscriptionRow[] }> {
    return client.get<{ rows: SubscriptionRow[] }>(
      `/billing/subscriptions${buildSubsQuery(params)}`,
    );
  },
  async changePlan(client: ApiClient, tenantId: string, planCode: string): Promise<SubscriptionRow> {
    return client.patch<SubscriptionRow>(
      `/billing/subscriptions/${tenantId}`,
      { planCode },
    );
  },
  async grantCredit(
    client: ApiClient,
    input: { tenantId: string; amountUsdMicro: number; reason: string },
  ): Promise<{ id: string; tenantId: string; amountUsdMicro: number; ts: string }> {
    return client.post('/billing/credits', input);
  },
  async listReconciliation(
    client: ApiClient,
    params: { status?: string; limit?: number } = {},
  ): Promise<{ rows: ReconciliationRow[] }> {
    const sp = new URLSearchParams();
    if (params.status) sp.set('status', params.status);
    if (params.limit !== undefined) sp.set('limit', String(params.limit));
    const tail = sp.toString();
    return client.get<{ rows: ReconciliationRow[] }>(
      `/billing/reconciliation${tail.length > 0 ? `?${tail}` : ''}`,
    );
  },
};
